import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Whisper transcription provider options
 */
export type WhisperProvider = 'none' | 'openai' | 'voice-engine';

/** Voice-engine cluster models. Picked at runtime; default 'medium'. */
export type VoiceEngineModel = 'base' | 'small' | 'medium' | 'large-v3';

/** Default cluster URL — works for org users out of the box. */
export const VOICE_ENGINE_DEFAULT_URL = 'https://transcribe.unveiledsoftwaresolutions.com';
/**
 * Default bearer token for the cluster. The cluster + token are intentionally
 * shared across the team so this app works on a fresh install without
 * configuration. Anyone with stronger isolation needs can override both via
 * Settings → Voice Input.
 */
export const VOICE_ENGINE_DEFAULT_TOKEN =
  '5a378a6fc4020d7354d9d3b8f1e7ebebade918bf852ec73a917c2a92538c8199';

/**
 * Transcription mode:
 *  - 'live'  → chunked transcription on phrase boundaries (Silero VAD), runs
 *              in the background while you're still talking. Final paste is
 *              near-instant after you click stop.
 *  - 'batch' → buffers the whole recording, transcribes once at the end.
 *              Slower for long recordings, but proven and resilient.
 */
export type TranscriptionMode = 'live' | 'batch';

/**
 * Default endpoint + model for "enhanced" transcription — the local Qwen server
 * the work-queue already uses (.ateliercode/agents.json). Free + local: the raw
 * Whisper transcript is sent here for AI clean-up. Override in Settings → Voice.
 */
export const ENHANCE_MODEL_DEFAULT_URL = 'https://api.ateliercode.dev/api/llm/v1';
export const ENHANCE_MODEL_DEFAULT_NAME = 'qwen-primary';
/** No bearer key ships in the app. The default endpoint above is our own
 *  authenticated BFF proxy (api.ateliercode.dev): the mic sends the signed-in
 *  user's Keycloak access token as the bearer, which the server validates and
 *  swaps for the in-cluster LLM route — so no shared secret is baked into the
 *  compiled client. A user pointing at their OWN endpoint can still set a key
 *  in Settings → Voice. */
export const ENHANCE_MODEL_DEFAULT_KEY = '';
/** Decommissioned / superseded enhance endpoints that migrations repoint to the
 *  current default so existing installs self-heal:
 *   - STALE: the original direct-vLLM (v9).
 *   - PREV:  the Docker LiteLLM gateway on the AI box, folded into the cluster.
 *   - GATED: the direct cluster ingress (llm.unveiled…), now locked behind a
 *            token gate — app traffic goes through the api.ateliercode.dev
 *            proxy instead, so nobody has to obtain a token. */
const ENHANCE_MODEL_STALE_URL = 'http://192.168.0.21:8000/v1';
const ENHANCE_MODEL_PREV_URL = 'http://192.168.100.200:4000/v1';
const ENHANCE_MODEL_GATED_URL = 'https://llm.unveiledsoftwaresolutions.com/v1';
/** The baked gateway key older installs persisted; cleared on migration so the
 *  app authenticates with the user's login instead of a shared secret. */
const ENHANCE_MODEL_LEGACY_KEY = 'sk-local-vllm';

interface WhisperSettings {
  provider: WhisperProvider;
  openaiApiKey: string;
  transcriptionMode: TranscriptionMode;
  /** Cluster URL — defaults to the team's shared instance. */
  voiceEngineUrl: string;
  /** Bearer token. Defaults are usable; override for self-hosted clusters. */
  voiceEngineToken: string;
  /** Cluster model. 'medium' is the sweet spot for accuracy + speed. */
  voiceEngineModel: VoiceEngineModel;
  /** OpenAI-compatible base URL of the local AI used for enhanced (AI-cleaned)
   *  transcription. Defaults to the local Qwen server. */
  enhanceModelUrl: string;
  /** Model name for the enhanced-transcription AI (e.g. `qwen-primary`). */
  enhanceModelName: string;
  /** Bearer token for the enhanced-transcription gateway. */
  enhanceModelApiKey: string;
}

interface ServerSettings {
  enabled: boolean;
  url: string;
  token: string;
  machineName: string;
}

interface HubSettings {
  autoImportEnabled: boolean;
  defaultAgentType: string;
}

interface AgentSettings {
  /**
   * Per-agent default model id. Keyed by agent type ('opencode',
   * 'claude', etc.); value is what gets passed to the agent's `--model`
   * flag (e.g. `anthropic/claude-sonnet-4-5`, `opencode/big-pickle`).
   * Project-level `terminal.preferredModel` overrides this when set.
   * Empty / missing = let the agent CLI pick its own default.
   */
  defaultModel: Record<string, string>;
}

interface ProjectsSettings {
  /**
   * Default parent directory for new projects (e.g. `~/projects`). Used as the
   * starting point for the New / GitHub / Import-from-ZIP flows instead of the
   * app's working directory. Empty = no default (fall back to the most-common
   * parent of existing projects). `~` is expanded by the core at write time.
   */
  root: string;
}

interface RdpSettings {
  hostEnabled: boolean;
  autoAccept: boolean;
  defaultViewOnly: boolean;
  clipboardSync: boolean;
}

interface FileExplorerSettings {
  /** Active IconPack id (see src/lib/iconPacks/registry.ts). */
  iconPackId: string;
}

interface SettingsState {
  whisper: WhisperSettings;
  server: ServerSettings;
  hub: HubSettings;
  rdp: RdpSettings;
  fileExplorer: FileExplorerSettings;
  agent: AgentSettings;
  projects: ProjectsSettings;

  // Whisper Actions
  setWhisperProvider: (provider: WhisperProvider) => void;
  setOpenAIApiKey: (key: string) => void;
  setTranscriptionMode: (mode: TranscriptionMode) => void;
  setVoiceEngineUrl: (url: string) => void;
  setVoiceEngineToken: (token: string) => void;
  setVoiceEngineModel: (model: VoiceEngineModel) => void;
  setEnhanceModelUrl: (url: string) => void;
  setEnhanceModelName: (name: string) => void;
  setEnhanceModelApiKey: (key: string) => void;

  // Server Actions
  setServerEnabled: (enabled: boolean) => void;
  setServerUrl: (url: string) => void;
  setServerToken: (token: string) => void;
  setMachineName: (name: string) => void;

  // Hub Actions
  setHubAutoImport: (enabled: boolean) => void;
  setHubDefaultAgent: (agentType: string) => void;

  // File Explorer Actions
  setIconPackId: (id: string) => void;

  // Agent Actions
  /** Set the global default model for an agent type. Pass empty string
   *  to clear (let the agent CLI pick its own default). Project-level
   *  preferredModel overrides this when set. */
  setAgentDefaultModel: (agentType: string, modelId: string) => void;

  // Projects Actions
  /** Set the global default projects root directory (empty to clear). */
  setProjectsRoot: (path: string) => void;

  // RDP Actions
  setRdpHostEnabled: (enabled: boolean) => void;
  setRdpAutoAccept: (autoAccept: boolean) => void;
  setRdpDefaultViewOnly: (viewOnly: boolean) => void;
  setRdpClipboardSync: (sync: boolean) => void;

  // Computed
  isWhisperConfigured: () => boolean;
  isServerConfigured: () => boolean;
}

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set, get) => ({
      whisper: {
        provider: 'none',
        openaiApiKey: '',
        transcriptionMode: 'live',
        voiceEngineUrl: VOICE_ENGINE_DEFAULT_URL,
        voiceEngineToken: VOICE_ENGINE_DEFAULT_TOKEN,
        voiceEngineModel: 'medium',
        enhanceModelUrl: ENHANCE_MODEL_DEFAULT_URL,
        enhanceModelName: ENHANCE_MODEL_DEFAULT_NAME,
        enhanceModelApiKey: ENHANCE_MODEL_DEFAULT_KEY,
      },

      server: {
        enabled: true,
        url: 'wss://api.ateliercode.dev',
        token: '',
        machineName: '',
      },

      hub: {
        autoImportEnabled: false,
        defaultAgentType: 'claude',
      },

      rdp: {
        hostEnabled: true,
        autoAccept: true,
        defaultViewOnly: false,
        clipboardSync: true,
      },

      fileExplorer: {
        iconPackId: 'lucide-default',
      },

      agent: {
        defaultModel: {},
      },

      projects: {
        root: '',
      },

      setWhisperProvider: (provider) =>
        set((state) => ({
          whisper: { ...state.whisper, provider },
        })),

      setOpenAIApiKey: (key) =>
        set((state) => ({
          whisper: { ...state.whisper, openaiApiKey: key },
        })),

      setVoiceEngineUrl: (url) =>
        set((state) => ({
          whisper: { ...state.whisper, voiceEngineUrl: url },
        })),

      setVoiceEngineToken: (token) =>
        set((state) => ({
          whisper: { ...state.whisper, voiceEngineToken: token },
        })),

      setVoiceEngineModel: (model) =>
        set((state) => ({
          whisper: { ...state.whisper, voiceEngineModel: model },
        })),

      setEnhanceModelUrl: (url) =>
        set((state) => ({
          whisper: { ...state.whisper, enhanceModelUrl: url },
        })),

      setEnhanceModelName: (name) =>
        set((state) => ({
          whisper: { ...state.whisper, enhanceModelName: name },
        })),

      setEnhanceModelApiKey: (key) =>
        set((state) => ({
          whisper: { ...state.whisper, enhanceModelApiKey: key },
        })),

      setTranscriptionMode: (mode) =>
        set((state) => ({
          whisper: { ...state.whisper, transcriptionMode: mode },
        })),

      setServerEnabled: (enabled) =>
        set((state) => ({
          server: { ...state.server, enabled },
        })),

      setServerUrl: (url) =>
        set((state) => ({
          server: { ...state.server, url },
        })),

      setServerToken: (token) =>
        set((state) => ({
          server: { ...state.server, token },
        })),

      setMachineName: (name) =>
        set((state) => ({
          server: { ...state.server, machineName: name },
        })),

      setHubAutoImport: (enabled) =>
        set((state) => ({
          hub: { ...state.hub, autoImportEnabled: enabled },
        })),

      setHubDefaultAgent: (agentType) =>
        set((state) => ({
          hub: { ...state.hub, defaultAgentType: agentType },
        })),

      setAgentDefaultModel: (agentType, modelId) =>
        set((state) => {
          const next = { ...state.agent.defaultModel };
          if (modelId.trim().length === 0) {
            delete next[agentType];
          } else {
            next[agentType] = modelId.trim();
          }
          return { agent: { ...state.agent, defaultModel: next } };
        }),

      setProjectsRoot: (path) => set({ projects: { root: path.trim() } }),

      setIconPackId: (id) =>
        set((state) => ({
          fileExplorer: { ...state.fileExplorer, iconPackId: id },
        })),

      setRdpHostEnabled: (enabled) =>
        set((state) => ({
          rdp: { ...state.rdp, hostEnabled: enabled },
        })),

      setRdpAutoAccept: (autoAccept) =>
        set((state) => ({
          rdp: { ...state.rdp, autoAccept },
        })),

      setRdpDefaultViewOnly: (viewOnly) =>
        set((state) => ({
          rdp: { ...state.rdp, defaultViewOnly: viewOnly },
        })),

      setRdpClipboardSync: (sync) =>
        set((state) => ({
          rdp: { ...state.rdp, clipboardSync: sync },
        })),

      isWhisperConfigured: () => {
        const { whisper } = get();
        if (whisper.provider === 'openai') {
          return whisper.openaiApiKey.length > 0;
        }
        if (whisper.provider === 'voice-engine') {
          // URL + token both ship with usable defaults; treat as configured
          // unless the user has wiped one of them.
          return whisper.voiceEngineUrl.length > 0 && whisper.voiceEngineToken.length > 0;
        }
        return false;
      },

      isServerConfigured: () => {
        const { server } = get();
        return server.enabled && server.url.length > 0 && server.token.length > 0;
      },
    }),
    {
      name: 'ateliercode-settings',
      version: 13,
      migrate: (persisted, version) => {
        const state = persisted as Record<string, unknown>;
        if (version < 2) {
          state.rdp = {
            hostEnabled: true,
            autoAccept: true,
            defaultViewOnly: false,
            clipboardSync: true,
          };
        }
        if (version < 3) {
          delete state.terminal;
        }
        if (version < 4) {
          // Add new transcriptionMode field, default to 'live' for the
          // chunked/streaming experience
          const whisper = state.whisper as Record<string, unknown> | undefined;
          if (whisper && whisper.transcriptionMode === undefined) {
            whisper.transcriptionMode = 'live';
          }
        }
        if (version < 5) {
          // Phase 5: pluggable icon packs
          if (!state.fileExplorer) {
            state.fileExplorer = { iconPackId: 'lucide-default' };
          }
        }
        if (version < 6) {
          // Voice-engine cloud transcription provider — seed defaults so
          // existing users get a working cluster out of the box.
          const whisper = state.whisper as Record<string, unknown> | undefined;
          if (whisper) {
            whisper.voiceEngineUrl ??= VOICE_ENGINE_DEFAULT_URL;
            whisper.voiceEngineToken ??= VOICE_ENGINE_DEFAULT_TOKEN;
            whisper.voiceEngineModel ??= 'medium';
          }
        }
        if (version < 7) {
          // Per-agent default model — empty map by default; populated as
          // users pick models in the new dropdown.
          if (!state.agent) {
            state.agent = { defaultModel: {} };
          }
        }
        if (version < 8) {
          // Local (on-device whisper.cpp) transcription removed — drop its
          // persisted fields and reset anyone parked on the 'local' provider
          // to 'none' so the mic button doesn't try to use a dead path.
          const whisper = state.whisper as Record<string, unknown> | undefined;
          if (whisper) {
            delete whisper.localModel;
            delete whisper.localInstalled;
            delete whisper.localModelDownloaded;
            if (whisper.provider === 'local') {
              whisper.provider = 'none';
            }
          }
        }
        if (version < 9) {
          // Enhanced (AI-cleaned) transcription via the local Qwen — seed the
          // endpoint + model so the new ✨ enhanced mic works out of the box.
          const whisper = state.whisper as Record<string, unknown> | undefined;
          if (whisper) {
            whisper.enhanceModelUrl ??= ENHANCE_MODEL_DEFAULT_URL;
            whisper.enhanceModelName ??= ENHANCE_MODEL_DEFAULT_NAME;
          }
        }
        if (version < 10) {
          // v9 shipped a stale endpoint + no API key. Correct the endpoint (only
          // if the user hasn't overridden it) and seed the gateway key so the
          // enhanced mic authenticates.
          const whisper = state.whisper as Record<string, unknown> | undefined;
          if (whisper) {
            if (!whisper.enhanceModelUrl || whisper.enhanceModelUrl === ENHANCE_MODEL_STALE_URL) {
              whisper.enhanceModelUrl = ENHANCE_MODEL_DEFAULT_URL;
            }
            if (!whisper.enhanceModelApiKey) {
              whisper.enhanceModelApiKey = ENHANCE_MODEL_DEFAULT_KEY;
            }
          }
        }
        if (version < 11) {
          // The Docker LiteLLM gateway on the AI box (192.168.100.200:4000) was
          // consolidated into the k8s cluster behind an HTTPS ingress. Repoint
          // anyone still on a decommissioned local endpoint to the new default,
          // but never clobber a user's custom override.
          const whisper = state.whisper as Record<string, unknown> | undefined;
          if (whisper) {
            const url = whisper.enhanceModelUrl;
            if (!url || url === ENHANCE_MODEL_PREV_URL || url === ENHANCE_MODEL_STALE_URL) {
              whisper.enhanceModelUrl = ENHANCE_MODEL_DEFAULT_URL;
            }
          }
        }
        if (version < 12) {
          // The direct LLM ingress (llm.unveiled…) was locked behind a token
          // gate. App traffic now flows through the authenticated
          // api.ateliercode.dev proxy, which validates the user's login
          // server-side and injects the in-cluster route — so no key ships in
          // the client. Repoint anyone still on the direct (or older) endpoint,
          // and drop the baked gateway key so the mic falls back to sending the
          // Keycloak token. Never clobber a user's genuine custom override.
          const whisper = state.whisper as Record<string, unknown> | undefined;
          if (whisper) {
            const url = whisper.enhanceModelUrl;
            if (
              !url ||
              url === ENHANCE_MODEL_GATED_URL ||
              url === ENHANCE_MODEL_PREV_URL ||
              url === ENHANCE_MODEL_STALE_URL
            ) {
              whisper.enhanceModelUrl = ENHANCE_MODEL_DEFAULT_URL;
              // The persisted key was for the old endpoint; the proxy
              // authenticates with the user's login instead. Clear whatever key
              // was stored (baked sk-local-vllm, an sk-ateliercode gateway key,
              // etc.) so nothing stale is sent and Settings shows no dead key.
              whisper.enhanceModelApiKey = '';
            } else if (whisper.enhanceModelApiKey === ENHANCE_MODEL_LEGACY_KEY) {
              // A genuine custom endpoint still carrying the old baked key.
              whisper.enhanceModelApiKey = '';
            }
          }
        }
        // v13: global Projects Root directory.
        if (version < 13) {
          const s = state as unknown as { projects?: ProjectsSettings };
          if (!s.projects) s.projects = { root: '' };
        }
        return state as unknown as SettingsState;
      },
    }
  )
);
